import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { SpaceType } from '@onesource/schemas';
import {
  getFeedSpacesQueryOptions,
  getSession,
  queryClient,
  useGetAllProfileQuery,
  useGetSpace,
  useGetSubSpaces,
} from '@onesource/services';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { hasCommunityAccess } from 'src/utils/app-access';

import WhosOnline from '#/components/whos-online';

import { CommunityTabContent } from '../../../../../features/community-hub/components/community-tab-content';
import { FeedSearchParamsSchema } from '../../../../../features/community-hub/components/feed-filter-bar';
import { FeedPostsSkeleton } from '../../../../../features/community-hub/components/feed-posts-skeleton';
import { FeedTabContent } from '../../../../../features/community-hub/components/feed-tab-content';
import { SpaceNoAccessPage } from '../../../../../features/community-hub/components/space-no-access-page';

/**
 * Space Community Route
 * Path: /community-hub/spaces/:id/community
 *
 * Handles three cases:
 * 1. If user has no access -> shows space name + banner with lock overlay
 * 2. If :id is a CONTAINER space -> shows sub-tabs for each FEED sub-space
 * 3. If :id is a FEED space -> shows posts directly from that feed
 */
export const Route = createFileRoute('/_protected/community-hub/spaces/$id/community')({
  validateSearch: FeedSearchParamsSchema,
  // Gate the community hub: users with no accessible community/feed space are
  // redirected home rather than landing on an empty/locked community route.
  beforeLoad: async () => {
    const feedSpaces = await queryClient.ensureQueryData(getFeedSpacesQueryOptions());
    if (!hasCommunityAccess(feedSpaces)) {
      throw redirect({ to: '/' });
    }
  },
  component: SpaceCommunityRoute,
});

function SpaceCommunityRoute() {
  const { id } = Route.useParams();
  const token = getSession();
  const { data: space, isLoading: isLoadingSpace, error: spaceError } = useGetSpace(id);
  const { data: profile } = useGetAllProfileQuery({ token });

  const isContainer = space?.spaceType === SpaceType.CONTAINER;

  // Access is determined by whether the user has an entry in profile.access for the space's app.
  // Sub-spaces don't carry app directly — inherit from parent. profile.access is built from user_access.
  const spaceApp = space?.app ?? space?.parent?.app;
  const hasAccess = spaceApp ? !!profile?.access?.[spaceApp] : false;

  const { data: subSpaces, isLoading: isLoadingSubSpaces } = useGetSubSpaces(id, {
    enabled: hasAccess && isContainer,
  });

  if (isLoadingSpace || (hasAccess && isContainer && isLoadingSubSpaces)) {
    return (
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: 'calc(100% - 250px)' } }}>
          <FeedPostsSkeleton />
        </Box>
        <WhosOnline />
      </Box>
    );
  }

  if (spaceError) {
    return <Alert severity="error">Failed to load space: {spaceError.message}</Alert>;
  }

  if (!space) {
    return <Alert severity="error">Space not found</Alert>;
  }

  // No access - show locked page
  if (!hasAccess) {
    return <SpaceNoAccessPage name={space.name} bannerImage={space.bannerImage ?? null} />;
  }

  // If this is a FEED space, show posts directly
  if (space.spaceType === SpaceType.FEED) {
    return <FeedTabContent subSpace={space} parentSpace={space} />;
  }

  // If this is a CONTAINER space, show sub-tabs for each FEED sub-space
  const feedSubSpaces = subSpaces?.filter(s => s.spaceType === SpaceType.FEED) || [];

  return <CommunityTabContent subSpaces={feedSubSpaces} parentSpace={space} />;
}
